import { useEffect, useRef, useState } from 'react';
import { getQuiz, submitQuiz, type QuizSubmitResult, type ScoreRead } from '../../api/endpoints.js';
import { useAction, useApi } from '../../api/hooks.js';
import { goHome, goStep } from '../../flow/nav.js';
import { quizQuestionApplies as applies } from '../../flow/quiz-gate.js';
import { Badge, Failure, Loading } from '../../ui/kit.js';
import {
  BrandPulse,
  FlowScreen,
  FlowTitle,
  OptionCard,
  PrimaryButton,
} from '../../ui/flow.js';

/**
 * Mobile screens 6–8: the stepped quiz, the 2.8-second calculating bridge, and
 * the score report. One question per screen, single-select answers advance by
 * themselves after 220 ms, and the conditional question appears only when its
 * trigger answer holds — all rendered from whatever GET /v1/quiz serves, since
 * the definition is data, not code.
 */

type Answers = Record<string, string | string[]>;

export function QuizStep({
  onComplete,
}: {
  onComplete: (quizVersion: string, answers: Answers) => void;
}) {
  const quiz = useApi(getQuiz, []);
  const [answers, setAnswers] = useState<Answers>({});
  const [index, setIndex] = useState(0);
  const advanceTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (advanceTimer.current !== null) window.clearTimeout(advanceTimer.current);
    },
    [],
  );

  if (quiz.error !== null) {
    return (
      <FlowScreen progress={30} onBack={() => window.history.back()}>
        <Failure error={quiz.error} />
      </FlowScreen>
    );
  }
  if (quiz.data === null) {
    return (
      <FlowScreen progress={30} onBack={() => window.history.back()}>
        <Loading />
      </FlowScreen>
    );
  }

  const { quiz_version, questions } = quiz.data;
  const visible = questions.filter((question) => applies(question, answers));
  const question = visible[index];
  if (question === undefined) {
    // Stepped past the end (a conditional question vanished): finish now.
    onComplete(quiz_version, answers);
    return <Loading />;
  }

  const total = visible.length;
  const progress = ((index + 1) / total) * 100;
  const multi = question.multi === true;
  const value = answers[question.key];
  const picked: string[] = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];

  const finishOrAdvance = (nextAnswers: Answers) => {
    const nextVisible = questions.filter((entry) => applies(entry, nextAnswers));
    if (index + 1 >= nextVisible.length) onComplete(quiz_version, nextAnswers);
    else setIndex(index + 1);
  };

  return (
    <FlowScreen
      progress={progress}
      onBack={() => {
        if (index === 0) window.history.back();
        else setIndex(index - 1);
      }}
      footer={
        multi ? (
          <PrimaryButton onClick={() => finishOrAdvance(answers)} disabled={picked.length === 0}>
            Continue
          </PrimaryButton>
        ) : undefined
      }
    >
      <p className="quiz-step-label">
        LIKENESS HEALTH QUIZ ({index + 1}/{total})
      </p>
      <FlowTitle>{question.prompt}</FlowTitle>
      {multi && <p className="flow-hint">Select all that apply</p>}
      <div className="option-stack">
        {question.options.map((option) => {
          const selected = picked.includes(option);
          return (
            <OptionCard
              key={option}
              selected={selected}
              checkbox={multi}
              onClick={() => {
                if (multi) {
                  const next = selected
                    ? picked.filter((entry) => entry !== option)
                    : [...picked, option];
                  setAnswers({ ...answers, [question.key]: next });
                  return;
                }
                const next = { ...answers, [question.key]: option };
                setAnswers(next);
                // Single-select advances by itself, after the selection shows.
                if (advanceTimer.current !== null) window.clearTimeout(advanceTimer.current);
                advanceTimer.current = window.setTimeout(() => finishOrAdvance(next), 220);
              }}
            >
              {option}
            </OptionCard>
          );
        })}
      </div>
    </FlowScreen>
  );
}

/**
 * Presentation and the actual POST at once. The mobile app fires the POST after
 * navigating away and parks failures in a retry queue; this client has no queue,
 * so the bridge waits for BOTH the 2.8-second beat and the server's answer —
 * the score shown next is the server's own, never a local computation.
 */
export function CalculatingStep({
  pending,
  onScored,
}: {
  pending: { quizVersion: string; answers: Answers } | null;
  onScored: (result: QuizSubmitResult) => void;
}) {
  const [beatDone, setBeatDone] = useState(false);
  const submit = useAction(submitQuiz);

  useEffect(() => {
    const timer = window.setTimeout(() => setBeatDone(true), 2800);
    return () => window.clearTimeout(timer);
  }, []);

  const ranRef = useRef(false);
  useEffect(() => {
    if (pending !== null && !ranRef.current) {
      ranRef.current = true;
      submit.run(pending.quizVersion, pending.answers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  useEffect(() => {
    if (beatDone && submit.result !== null) onScored(submit.result);
  }, [beatDone, submit.result, onScored]);

  if (submit.error !== null) {
    return (
      <FlowScreen onBack={() => goStep('quiz', { replace: true })}>
        <FlowTitle>We couldn&rsquo;t save your answers</FlowTitle>
        <Failure error={submit.error} />
        <PrimaryButton onClick={() => goStep('quiz', { replace: true })}>
          Back to the quiz
        </PrimaryButton>
      </FlowScreen>
    );
  }

  return (
    <FlowScreen>
      <div className="flow-center">
        <FlowTitle>
          Calculating your
          <br />
          Likeness Health Score℠
        </FlowTitle>
        <BrandPulse />
      </div>
    </FlowScreen>
  );
}

const BANDS = [
  { key: 'low', label: 'Low', range: '80-100', color: '#39BEB7' },
  { key: 'moderate', label: 'Moderate', range: '50-79', color: '#FFB020' },
  { key: 'high', label: 'High', range: '0-49', color: '#FF3B5C' },
] as const;

function bandOf(band: string): (typeof BANDS)[number] {
  return BANDS.find((entry) => entry.key === band.toLowerCase()) ?? BANDS[1];
}

/** A point on the gauge's half-ring arc, `fraction` from the left (15,95) end
 *  to the right (155,95) end, going over the top — the same arc the two
 *  <path> strokes below are drawn on. */
function arcPoint(fraction: number, radius: number): { x: number; y: number } {
  const theta = Math.PI * (1 - Math.max(0, Math.min(1, fraction)));
  return { x: 85 + radius * Math.cos(theta), y: 95 - radius * Math.sin(theta) };
}

/** The half-ring gauge, filled to the score in the band's color. `ceiling`
 *  draws a small radial tick at ceiling/100 — the gap-messaging visual for
 *  "of a possible N today", never a second number invented on top of it. */
function ScoreGauge({ score, color, ceiling }: { score: number; color: string; ceiling?: number }) {
  const clamped = Math.max(0, Math.min(100, score));
  const radius = 70;
  const circumference = Math.PI * radius;
  const ceilingFraction = ceiling === undefined ? null : ceiling / 100;
  const tickInner = ceilingFraction === null ? null : arcPoint(ceilingFraction, radius - 10);
  const tickOuter = ceilingFraction === null ? null : arcPoint(ceilingFraction, radius + 10);
  return (
    <svg width="170" height="100" viewBox="0 0 170 100" aria-hidden="true">
      <path
        d="M 15 95 A 70 70 0 0 1 155 95"
        fill="none"
        stroke="#EBE6FA"
        strokeWidth="12"
        strokeLinecap="round"
      />
      <path
        d="M 15 95 A 70 70 0 0 1 155 95"
        fill="none"
        stroke={color}
        strokeWidth="12"
        strokeLinecap="round"
        strokeDasharray={`${String((clamped / 100) * circumference)} ${String(circumference)}`}
      />
      {tickInner !== null && tickOuter !== null && (
        <line
          x1={tickInner.x}
          y1={tickInner.y}
          x2={tickOuter.x}
          y2={tickOuter.y}
          stroke="#380E99"
          strokeWidth="3"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

export function ScoreStep({ score }: { score: ScoreRead }) {
  const [showActions, setShowActions] = useState(false);
  const data = score.score;
  const band = bandOf(data.band.replace(/ risk$/, ''));
  const total = data.live;
  const milestoneDays = data.breakdown.escrow.next_milestone_days ?? 90;

  return (
    <FlowScreen
      progress={52}
      onBack={() => window.history.back()}
      onClose={goHome}
      footer={<PrimaryButton onClick={() => goStep('plans')}>Continue</PrimaryButton>}
    >
      <FlowTitle>Your Initial Likeness Health Report℠</FlowTitle>

      <div className="score-card">
        <p className="score-card-label">Your Likeness Health Score℠</p>
        <div className="score-card-main">
          <div>
            <span className="score-number" style={{ color: band.color }}>
              {total}
            </span>
            <dl className="score-legend">
              {BANDS.map((entry) => (
                <div key={entry.key} className="score-legend-row">
                  <dt>
                    <span className="score-dot" style={{ background: entry.color }} />
                    {entry.label}
                  </dt>
                  <dd>{entry.range}</dd>
                </div>
              ))}
            </dl>
          </div>
          <ScoreGauge score={total} color={band.color} ceiling={data.current_ceiling} />
        </div>
      </div>

      <div className="score-card">
        <p className="score-card-label">What does my score indicate?</p>
        <p className="flow-sub" style={{ margin: 0 }}>
          {/* data.band is rendered VERBATIM ("low risk"/"moderate risk"/"high
              risk") — never band.label, which is BANDS' own static English
              and legitimate only for the gauge's colour and legend, not for
              a sentence claiming to describe the server's classification. */}
          A Likeness Health Score℠ of <strong>{total}</strong> indicates that you are at{' '}
          <strong>{data.band}</strong> for likeness theft and misuse online. You&rsquo;re at{' '}
          {total} of a possible {data.current_ceiling} today — your ceiling rises to{' '}
          {data.maximum_ceiling} after {milestoneDays} days of monitoring.
        </p>
        {/* Scope note, served verbatim, rendered where the number lands —
            never paraphrased, truncated or softened: a high score is never
            an all-clear, and this is the first score a user ever sees. */}
        <p className="flow-note" style={{ margin: '10px 0 0' }}>{score.scope_note}</p>
      </div>

      <button type="button" className="flow-link" onClick={() => setShowActions(!showActions)}>
        {showActions ? 'Hide recommended actions' : 'View recommended actions'}
      </button>
      {showActions && (
        <div className="score-card">
          <p className="score-card-label">Here are the factors affecting your score</p>
          {data.breakdown.quiz.map((entry) => (
            <div key={entry.key} className="score-factor">
              <div className="score-factor-head">
                <strong>{entry.key}</strong>
                <span className="flow-note" style={{ margin: 0 }}>
                  {entry.value} · −{entry.deduction}
                </span>
              </div>
              <Badge>{entry.type}</Badge>
            </div>
          ))}
          {data.breakdown.dynamic
            .filter((entry) => entry.active)
            .map((entry) => (
              <div key={entry.id} className="score-factor">
                <div className="score-factor-head">
                  <strong>{entry.id}</strong>
                  <span className="flow-note" style={{ margin: 0 }}>−{entry.points}</span>
                </div>
              </div>
            ))}
        </div>
      )}
      <p className="flow-note">
        Your completion of our recommended actions will raise your Likeness Health Score℠.
      </p>
    </FlowScreen>
  );
}
